import React, { useEffect, useState } from 'react';
import { StyledButton } from '../styles/calendarIntegration.styles';
import { CALENDAR_PROVIDERS } from './calendarProviders';
import { formatDistanceToNow } from 'date-fns';
import { calendarIntegrationService } from '@/_services/api/calendarIntegration';
import { toast } from 'react-toastify';
import { VscLoading } from 'react-icons/vsc';
import { connect } from 'react-redux';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';
import ExternalCalendarAccountSettings from './ExternalCalendarAccountSettings';

const Component = ({ account, fetchAccounts, teams = [] }) => {
    const [isBusy, setIsBusy] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [formState, setFormState] = useState({
        syncDirection: account.syncDirection,
        activityLibraryChatroom: account.activityLibraryChatroom || null,
        syncFutureLimit: account?.syncSettings?.syncFutureLimit || null,
        hideFromCalendar: account?.syncSettings?.hideFromCalendar || false,
        syncCopiedTasks: account?.syncSettings?.syncCopiedTasks || false,
    });
    const [selectedChannelOption, setSelectedChannelOption] = useState(null);
    const provider = CALENDAR_PROVIDERS[account.calendarProvider];
    const Icon = provider?.icon;

    useEffect(() => {
        setFormState({
            syncDirection: account.syncDirection,
            activityLibraryChatroom: account.activityLibraryChatroom || null,
            syncFutureLimit: account?.syncSettings?.syncFutureLimit || null,
            hideFromCalendar: account?.syncSettings?.hideFromCalendar || false,
            syncCopiedTasks: account?.syncSettings?.syncCopiedTasks || false,
        });
        if (account.activityLibraryChatroom && account.activityLibraryChatroom?._id) {
            setSelectedChannelOption({
                value: account.activityLibraryChatroom?._id,
                label: account.activityLibraryChatroom.title,
            });
        }
    }, [account._id, account.syncDirection, account.activityLibraryChatroom, account?.syncSettings]);

    const handleSaveSettings = async () => {
        try {
            setIsSaving(true);
            await calendarIntegrationService.updateCalendarSyncSettings({
                accountId: account._id,
                settings: {
                    syncDirection: formState.syncDirection,
                    activityLibraryChatroom: (selectedChannelOption || {})?.value || null,
                    syncFutureLimit: formState.syncFutureLimit,
                    hideFromCalendar: formState.hideFromCalendar || false,
                    syncCopiedTasks: formState.syncCopiedTasks || false,
                },
            });
            toast.success('Calendar settings updated.');
            await fetchAccounts();
        } catch (error) {
            toast.error(error?.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleManualSync = async (accountId) => {
        try {
            setIsSyncing(true);
            await calendarIntegrationService.syncCalendar({ accountId });
            toast.success('Manual sync has been started.');
            await fetchAccounts();
        } catch (error) {
            toast.error(error?.message);
        } finally {
            setIsSyncing(false);
        }
    };

    const handleDisconnect = async (accountId) => {
        try {
            setIsBusy(true);
            await calendarIntegrationService.disconnectCalendar({ accountId });
            toast.success('Calendar disconnected successfully.');
            await fetchAccounts();
        } catch (error) {
            toast.error(error?.message);
        } finally {
            await fetchAccounts();
            setIsBusy(false);
        }
    };
    return (
        <div className="d-flex flex-column  mb-4 rounded" style={{ border: '1px solid #ccc' }}>
            <div className="d-flex flex-row justify-content-between p-3">
                <div className="d-flex flex-row w-100">
                    <div>{Icon}</div>
                    <div className="d-flex flex-column ml-4 w-100">
                        <div>{provider?.name}</div>
                        <div className="text-muted">{account?.email}</div>

                        <div>
                            {account.lastSyncAt && (
                                <p className="text-muted small mb-2">
                                    Last synced:&nbsp;
                                    {formatDistanceToNow(new Date(account.lastSyncAt), { addSuffix: true })}
                                </p>
                            )}
                        </div>
                        {/* <hr /> */}
                    </div>
                </div>
                <div>
                    <span className={`badge ${account?.archived ? 'badge-danger' : 'badge-success'}`}>
                        {account?.archived ? 'Disconnected' : 'Connected'}
                    </span>
                </div>
            </div>
            <div style={{ borderTop: '1px solid #ccc' }}>
                <ExternalCalendarAccountSettings
                    account={account}
                    teams={teams}
                    formState={formState}
                    setFormState={setFormState}
                    isBusy={isBusy}
                    isSaving={isSaving}
                    isSyncing={isSyncing}
                    selectedChannelOption={selectedChannelOption}
                    setSelectedChannelOption={setSelectedChannelOption}
                    handleSaveSettings={handleSaveSettings}
                />
            </div>
            <div className="card-footer d-flex justify-content-between">
                <StyledButton
                    variant="outline-secondary"
                    onClick={() => handleManualSync(account._id)}
                    disabled={isBusy || isSaving || isSyncing}
                >
                    Sync Now
                    {isSyncing && <VscLoading className="spin ml-2" />}
                </StyledButton>
                <StyledButton
                    variant="outline-danger"
                    onClick={() => handleDisconnect(account._id)}
                    disabled={isBusy || isSaving || isSyncing}
                >
                    Disconnect
                    {isBusy && <VscLoading className="spin ml-2" />}
                </StyledButton>
            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({
    position: state.user.position,
    currUser: state.user.user,
    teams: state.teams.teams,
});

const CalendarProviderCard = (props) => (
    <ComponentErrorBoundary>
        <Component {...props} />
    </ComponentErrorBoundary>
);

export default connect(mapStateToProps, {})(CalendarProviderCard);
